/**
 * Admin Dashboard API Client
 * Fetches admin monitoring data from the API
 */

import type {
  OverviewStats,
  TenderLinksResponse,
  TenderLinkDetail,
  ParcelsResponse,
  AdminParcelDetail,
  SourcesResponse,
  AdminFilters,
} from '@tender-monitor/shared/types/admin';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

function buildQueryString(filters: AdminFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  return params.toString();
}

// Overview
export async function fetchOverview(): Promise<OverviewStats> {
  return fetchJson(`${API_BASE_URL}/api/admin/overview`);
}

// Tender Links
export async function fetchTenderLinks(filters: AdminFilters = {}): Promise<TenderLinksResponse> {
  const query = buildQueryString(filters);
  return fetchJson(`${API_BASE_URL}/api/admin/tender-links${query ? `?${query}` : ''}`);
}

export async function fetchTenderLinkDetail(id: string): Promise<TenderLinkDetail> {
  return fetchJson(`${API_BASE_URL}/api/admin/tender-links/${id}`);
}

// Parcels
export async function fetchAdminParcels(filters: AdminFilters = {}): Promise<ParcelsResponse> {
  const query = buildQueryString(filters);
  return fetchJson(`${API_BASE_URL}/api/admin/parcels${query ? `?${query}` : ''}`);
}

export async function fetchAdminParcelDetail(id: string): Promise<AdminParcelDetail> {
  return fetchJson(`${API_BASE_URL}/api/admin/parcels/${id}`);
}

// Sources
export async function fetchAdminSources(): Promise<SourcesResponse> {
  return fetchJson(`${API_BASE_URL}/api/admin/sources`);
}

export type SourceToggleResponse = {
  id: string;
  name: string;
  isActive: boolean;
};

export async function toggleSourceStatus(
  sourceId: string,
  isActive: boolean
): Promise<SourceToggleResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/sources/${sourceId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ isActive }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

// Crawl Trigger Response Types
export type CrawlTriggerResponse = {
  success: boolean;
  sourceId: string;
  sourceName: string;
  message: string;
};

export type CrawlAllTriggerResponse = {
  success: boolean;
  sourceCount: number;
  message: string;
};

// Crawl Triggers
export async function triggerSourceCrawl(
  sourceId: string
): Promise<CrawlTriggerResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/crawl/${sourceId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function triggerAllCrawl(): Promise<CrawlAllTriggerResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/crawl`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

// Create Source
export type CreateSourceInput = {
  name: string;
  type: 'gmina' | 'powiat';
  voivodeship: 'zachodniopomorskie' | 'lubuskie';
  url: string;
  teryt_code: string;
};

export type CreateSourceResponse = {
  source: {
    id: string;
    name: string;
    type: string;
    voivodeship: string;
    url: string;
    teryt_code: string;
    is_active: boolean;
    origin: string;
    created_at: string;
  };
};

export async function createSource(data: CreateSourceInput): Promise<CreateSourceResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/sources`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}
